import { PortalHost, PortalProvider } from "@gorhom/portal";
import * as Sentry from "@sentry/react-native";
import { RefreshCw } from "@tamagui/lucide-icons";
import { useFonts } from "expo-font";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Dimensions, type LayoutRectangle, LogBox } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import Confetti from "react-native-reanimated-confetti";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { TamaguiProvider, Theme, View, XStack, YStack } from "tamagui";
import { ApiProvider, useApi } from "./src/core/apis";
import { auth, notifySiteError } from "./src/core/firebaseInit";
import { initTranslations } from "./src/core/i18nInit";
import { showConfettiState } from "./src/core/states.ts/showConfetti";
import { StoresProvider, useStores } from "./src/core/stores";
import { trackEvent } from "./src/core/trackEvent";
import { isRunningInApp } from "./src/core/utils";
import { DevDbStats } from "./src/dev/DevDbStats";
import {
  sendMessageToReactNative,
  useWebViewBridge,
} from "./src/native/webapplogic";
import { MessageType } from "./src/native/webviewtypes";
import { Body } from "./src/ui/Body";
import { Box } from "./src/ui/Box";
import { Button } from "./src/ui/Button";
import { FullScreenLoading } from "./src/ui/Loading";
import { log } from "./src/ui/log";
import { Navigator } from "./src/ui/navigation/Navigation";
import { Pill, PillType } from "./src/ui/Pill";
import { Separator } from "./src/ui/Separator";
import { Subtext } from "./src/ui/Subtext";
import { Title } from "./src/ui/Title";
import { ToastProvider } from "./src/ui/Toast";
import config from "./tamagui.config";

Sentry.init({
  enabled: !__DEV__,
  dsn: "https://4566533a8dd5da385c3593378cb4cdd4@o61762.ingest.us.sentry.io/4507157060124672",
  debug: false, // If `true`, Sentry will try to print out useful debugging information if something goes wrong with sending the event. Set it to `false` in production

  // Configure Session Replay
  /*replaysSessionSampleRate: 0.5,
        replaysOnErrorSampleRate: 1,
        integrations: [Sentry.replayIntegration()],*/
  // uncomment the line below to enable Spotlight (https://spotlightjs.com)
  // spotlight: __DEV__,
});

// this provides some helpful reset styles to ensure a more consistent look
// only import this from your web app, not native
//import '@tamagui/core/reset.css';

const { width: windowWidth } = Dimensions.get("window");

require("./assets/planira_ogimage.png");

initTranslations();
LogBox.ignoreLogs(["Require cycle:"]);

const AppInit = observer(() => {
  const { sessionStore, superUsersStore, devsStore } = useStores();
  const { superUsersApi } = useApi();
  const [bottomLayout, setBottomLayout] = useState<
    LayoutRectangle | undefined
  >();

  if (devsStore.gotError) {
    throw devsStore.gotError;
  }

  useWebViewBridge();

  const [gotAuthState, setGotAuthState] = React.useState(false);
  const [fetchingToken, setFetchingToken] = React.useState(false);

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUserData) => {
      if (fbUserData) {
        const uid = fbUserData.uid;
        log.network("User signed in signIn success", uid);
        sessionStore.setAuthUser(fbUserData);

        // Check if already available in store first
        let superUser =
          superUsersStore.byId.get(uid) || (await superUsersApi.getById(uid));

        if (!superUser) {
          superUser = await superUsersApi.create(uid, {
            displayName: "",
            avatarUrl: "",
            email: fbUserData.email ?? "",
            businessPlans: [],
            plans: [],
            isDev: false,
            archivedPlans: [],
            expoPushToken: null,
          });
        }

        if (fbUserData.email && superUser && !superUser.data.email) {
          superUser.verifySuperUserEmail();
        }
      } else {
        log.network("User signed out");
      }
      setGotAuthState(true);
    });
    return () => {
      unsub();
    };
  }, [sessionStore, superUsersApi, superUsersStore]);

  React.useEffect(() => {
    if (!sessionStore.isLoggedIn && gotAuthState) {
      if (isRunningInApp()) {
        // Ask for token to be generated
        sendMessageToReactNative({ type: MessageType.RequestSignInToken });
        setFetchingToken(() => true);
      } else {
        signInAnonymously(auth)
          .then(() => {
            log.network("User signed in signInAnonymously started");
          })
          .catch((error) => {
            log.error(
              "User signInAnonymously in error",
              error.code,
              error.message,
            );
          });
      }
    }
  }, [gotAuthState, sessionStore]);

  if (!sessionStore.isLoggedIn) {
    let subtitle: string | undefined = "Soon...";
    if (fetchingToken) {
      subtitle = "Signing in";
    } else if (gotAuthState) {
      subtitle = "Hold on!";
    }

    return <FullScreenLoading subtitle={subtitle} />;
  }

  return (
    <React.Suspense fallback={<FullScreenLoading />}>
      <GestureHandlerRootView
        style={{ flex: 1, paddingBottom: bottomLayout?.height }}
      >
        <Navigator />
        <View
          onLayout={(event) => {
            setBottomLayout(event.nativeEvent.layout);
          }}
          style={{ position: "fixed", bottom: 0, left: 0, right: 0 }}
          zIndex={10}
        >
          <PortalHost name="bottomContent" />
        </View>
      </GestureHandlerRootView>

      {showConfettiState.get() && (
        <Confetti count={100} origin={{ x: windowWidth / 2, y: 0 }} fadeOut />
      )}
    </React.Suspense>
  );
});

const FallbackRender = ({ error }: { error: Error }) => {
  React.useEffect(() => {
    trackEvent(`error-${error.name}-${error.message}`);

    // Notify slack channel
    void notifySiteError(error);

    // Send custom report to Sentry
    Sentry.captureException(error, {
      tags: { section: "SiteError" }, // Add custom tags
      extra: { message: error.message, name: error.name }, // Add extra context
    });
  }, [error]);

  return (
    <View alignItems="center" flexGrow={1} margin="$large">
      <Box>
        <YStack gap="$medium">
          <Title>Error</Title>
          <Body>Something is broken 💥</Body>
          <Subtext>We will look into this as fast as possible.</Subtext>
          <Separator />
          <XStack gap="$medium">
            <Pill type={PillType.Danger} numberOfLines={0}>
              {error.name} - {error.message}
            </Pill>
          </XStack>
          <Body>Reload the page and try again 🤞</Body>

          <Button
            onPress={() => {
              document.location.reload();
            }}
            icon={RefreshCw}
          >
            Reload
          </Button>
        </YStack>
      </Box>
    </View>
  );
};

const App = observer(() => {
  const [loaded, _error] = useFonts({
    Inter: require("@tamagui/font-inter/otf/Inter-Medium.otf"),
    InterBold: require("@tamagui/font-inter/otf/Inter-Bold.otf"),
  });

  React.useEffect(() => {
    if (loaded) {
      // can hide splash screen here
    }
  }, [loaded]);

  if (!loaded) {
    return null;
  }

  return (
    <TamaguiProvider defaultTheme="light" config={config}>
      <Theme name={"light"}>
        <ErrorBoundary FallbackComponent={FallbackRender}>
          <SafeAreaProvider>
            <ToastProvider>
              <StoresProvider>
                <ApiProvider>
                  <PortalProvider shouldAddRootHost={false}>
                    <AppInit />

                    <View
                      style={{ position: "fixed", bottom: 0, width: "100%" }}
                    >
                      <DevDbStats />
                    </View>
                  </PortalProvider>
                </ApiProvider>
              </StoresProvider>
            </ToastProvider>
          </SafeAreaProvider>
        </ErrorBoundary>
      </Theme>
    </TamaguiProvider>
  );
});

export default Sentry.wrap(App);
